import {inject, Injectable, Injector, runInInjectionContext} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {BehaviorSubject, combineLatest, forkJoin, from, Observable, of} from 'rxjs';
import {debounceTime, filter, first, map, switchMap, tap} from 'rxjs/operators';

import {
    addDoc,
    collection,
    collectionData,
    deleteDoc,
    doc,
    docData,
    DocumentReference,
    Firestore,
    getDoc,
    getDocs,
    limit,
    orderBy,
    query,
    runTransaction,
    serverTimestamp,
    setDoc,
    updateDoc,
    where,
    writeBatch
} from '@angular/fire/firestore';
import {ConfigService} from './config.service';
import {GoogleApiService} from './google-api.service';

import moment from 'moment-timezone';
import {User} from '../users/interfaces/users';
import {Activity, Budget, GeneratedBy, Material, SubBudget} from '../interfaces';
import {Settings} from '../interfaces/settings';
import {Storage} from '../interfaces/storage';
import {Timereport} from '../timereport/interfaces';
import {Ddt} from '../ddt/interfaces/ddt';
import {AclUser, AppUser, AppUserUtil} from '../interfaces/app-user';
import {Company} from '../interfaces/address';
import {SubBudgetModel} from '../budget/models/sub-budget';
import {calculateCustomerFromHostname, sortActivities} from '../util';
import {InvoicesLIstFilter} from './invoices-list-table.service';
import {TimereportListFilter} from './timereport-list-table.service';
import {BudgetListFilter} from './budget-list-table.service';
import {DdtListFilter} from './ddt-list-table.service';
import {SummaryBalanceWrapper} from '../interfaces/summaryBalance';
import {ActivitiesTemplate, MarginsTemplate} from '../interfaces/templates';
import {Opportunity} from '../opportunities/interfaces/opportunity.interface';
import {FollowUp} from '../opportunities/interfaces/follow-up.interface';
import {Invoice} from '../interfaces/invoice';
import {StatReport} from '../interfaces/stats';
import {PaymentMethods} from '../interfaces/payment-methods';
import {AuthService} from './auth.service';



const TIMERREPORT_COLLECTION = 'TimeReportCollection';
const EMAILREPORT_COLLECTION = 'TimeReportEmail';
const STATREPORTS_COLLECTION = 'StatReportCollection';
const STATFIELDS_COLLECTION = 'StatFields';
const INVOICES_COLLECTION = 'Invoices';
const DDT_COLLECTION = 'Ddt';
const BUDGET_COLLECTION = 'BudgetCollection';
const SUMMARY_COLLECTION = 'SummaryCollection';
const STORAGE_COLLECTION = 'StorageCollection';
const SUBBUDGET_COLLECTION = 'sub-budgets';
const PAYMENTMETHODS_COLLECTION = 'PaymentMethods';
const USER_COLLECTION = 'Users';
const ROLE_COLLECTION = 'Roles';
const OPPORTUNITIES_COLLECTION = 'Opportunities';
const FOLLOW_UPS_COLLECTION = 'Follow-ups';
const BUDGET_RECALCULATE_COLLECTION = 'BudgetSummaryRecalculate';
const DELETED_BUDGETS = 'deletedBudgetCollection';

@Injectable({providedIn: 'root'})
export class DataService {

    private readonly cDR: DocumentReference<any>;
    private userCollectionRef;
    private activitiesTemplateCollRef;
    private activitiesTemplate$ = new BehaviorSubject<ActivitiesTemplate | null>(null);
    private opportunities$: Observable<Opportunity[]>;

    private settings: Settings | null = null;

    private auth: AuthService = inject(AuthService);
    private firestore: Firestore = inject(Firestore);
    private configService: ConfigService = inject(ConfigService);
    private http: HttpClient = inject(HttpClient);
    private googleApiService: GoogleApiService = inject(GoogleApiService);
    private injector: Injector = inject(Injector);

    /** Helper to wrap any AngularFire call inside this service's injection context */
    private inContext<T>(fn: () => T): T {
        return runInInjectionContext(this.injector, fn);
    }

    constructor() {

        const CUSTOMER_DOMAIN = calculateCustomerFromHostname(document.location.hostname);
        console.log('Dataservice constructor. setting customerDocumentReference. Domain inferred from the URL: ', this.firestore, CUSTOMER_DOMAIN);
        this.cDR = this.inContext(() => doc(this.firestore, 'Customers', CUSTOMER_DOMAIN));

        this.userCollectionRef = this.inContext(() => collection(this.cDR, 'Users')); // a ref to the todos collection

        // Subscribe to Activity templates
        this.activitiesTemplateCollRef = this.inContext(() => collection(this.cDR, '/Blueprints/ActivitiesTemplate/ActivitiesHistory'));
        this.inContext(() => collectionData(query(this.activitiesTemplateCollRef, where('latest', '==', true)))).pipe(
            map((aat: any) => aat[0]))
            // .do((aat) => console.log('aat', aat))
            .subscribe(this.activitiesTemplate$);

        // Subscrbe to settings
        this.configService.settings.subscribe((sett) => {
            this.settings = sett;
        });

        this.opportunities$ = this.inContext(() => collectionData(collection(this.cDR, OPPORTUNITIES_COLLECTION), {idField: 'id'})) as Observable<Opportunity[]>;

        console.log('end Dataservice constructor. ');
    }

    public getCountries() {
        const docRef = this.inContext(() => collectionData(collection(this.cDR, 'Countries'))) as Observable<{
            name: string
        }[]>;
        return docRef.pipe(
            map((response: { name: string }[]) =>
                response.map(c => c.name).sort((a, b) => a.localeCompare(b))
            )
        );
    }

    public getExchangeRateInternal(currency: string) {

        return this.auth.getUserIdToken().pipe(
            first(),
            switchMap((tid) => {
                const sett = this.configService.settings.value;
                const endPoint = sett.value.firebaseApi + '/budget/exchangeRate/' + currency;
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.auth.appDomain
                });
                if (tid) {
                    return this.http.get<{ rates: any[] }>(endPoint, {
                        headers: headers
                    });
                } else {
                    throw Error('No token');
                }
            }));
    }

    public prepareOffer(opt: object, scriptId: string): Observable<any> {
        if (!scriptId) {
            throw new Error('No script ID defined!');
        }
        return this.googleApiService.runScript(scriptId, 'compilaOfferta', [opt]);
    }

    // This could fail, settings can be null
    public getItemCategories = () => {
        return this.configService.settings.pipe(
            map((s) => s?.value?.categories));
    }

    public runSummaryRecalculateJob = (budgetId: string, exportId?: string) => {
        if (!budgetId) {
            throw Error('Budget Id is not defined');
        }

        return this.auth.getUserIdToken().pipe(
            first(),
            switchMap((tid) => {
                const sett = this.configService.settings.value;
                const endPoint = sett.value.firebaseApi + '/budget/runSummaryRecalculateJob/' + this.auth.appDomain + '/' + budgetId;
                // console.log('Got tid', tid);
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.auth.appDomain
                });

                if (tid) {
                    return this.http.post(endPoint, {exportId}, {
                        responseType: 'json',
                        headers: headers
                    });
                } else {
                    throw Error('No token');
                }
            }));
    }

    public getBudgetPaymentStatus = (budgetId: string, projectNr: string, finalOffer: number, domainId: string, companyId: string) => {
        return this.auth.getUserIdToken().pipe(
            first(),
            switchMap((tid) => {
                const sett = this.configService.settings.value;
                const endPoint = sett.value.firebaseApi + '/budget/paymentStatus/' + budgetId + '/' + projectNr +
                    '/' + finalOffer + '/' + domainId + '/' + companyId;
                // console.log('Got tid', tid);
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.auth.appDomain
                });
                // headers.append('Authorization', `Bearer ${tid}`);
                // headers.append('X-App-Domain', this.auth.appDomain);
                if (tid) {
                    return this.http.get(endPoint, {
                        responseType: 'text',
                        headers: headers
                    });
                } else {
                    throw Error('No token');
                }
            }));
    }

    // Query Prosperworks CRM <-- should we make a Pw Module?
    // TODO change in get contact api
    public getCompanyById = (companyId: string | number) => {

        return forkJoin([
            this.auth.getUserIdToken().pipe(first()),
            this.configService.settings.pipe(filter((value) => value != null), first())
        ]).pipe(
            first(),
            switchMap(([tid, sett]) => {
                const endPoint = sett.value.prosperworks.endpoint + '/companies/';
                const headers = {
                    'X-App-Domain': this.auth.appDomain,
                    'Authorization': `Bearer ${tid}`,
                    ...sett.value.prosperworks.headers // This is here only for backward compatibility
                };

                return this.http.get<Company>(endPoint + companyId, {
                    headers: new HttpHeaders(headers)
                });
            }), first());
    }

    // Query Prosperworks CRM <-- should we make a Pw Module?
    // TODO change in get con tact api
    public getPersonById = (personId: number) => {
        return forkJoin([
            this.auth.getUserIdToken().pipe(first()),
            this.configService.settings.pipe(filter((value) => value != null), first())
        ]).pipe(first(),
            switchMap(([tid, sett]) => {
                const endPoint = sett.value.prosperworks.endpoint + '/people/';
                const headers = {
                    'X-App-Domain': this.auth.appDomain,
                    'Authorization': `Bearer ${tid}`,
                    ...sett.value.prosperworks.headers // This is here only for backward compatibility
                };
                headers['X-App-Domain'] = this.auth.appDomain;
                return this.http.get<Company>(endPoint + personId, {
                    headers: new HttpHeaders(headers)
                });
            }),
            first(),
            tap((result) => console.log('getPersonById result: ', result)));
    }

    // TODO change in get con tact api
    public getFilteredCompanies = (q: string) => {
        return this.http.get<Company[]>('https://api.extravega.wonder-sys.com/api/company?q=' + q);
    }

    // TODO change in get con tact api
    public getCompany = (id: string) => {
        return this.http.get<Company[]>('https://api.extravega.wonder-sys.com/api/company/' + id);
    }

    // TODO change in get con tact api
    public getPeopleByCompany = (id: string) => {
        return this.http.get<Company[]>('https://api.extravega.wonder-sys.com/api/peopleByCompany/' + id);
    }

    public getAppUsers() {
        const docRef = this.inContext(() => collectionData(this.userCollectionRef, {idField: 'id'})) as Observable<AclUser[]>;
        return docRef
            .pipe(
                map((users: AclUser[]) => {
                    return users.filter(u => !this.auth.isASysAdmin(u));
                }));
    }

    public getActiveAppUsers() {
        return this.getAppUsers()
            .pipe(
                map(users => {
                    return users.filter(u => !u.deleted)
                        .sort((a, b) => a.email > b.email ? 1 : -1);
                }));
    }

    public getBudgetsListPaginated(pageSize: number, startAt: number, filterQuery: BudgetListFilter) {
        return combineLatest([this.auth.getUserIdToken(), this.configService.settings.pipe(filter(s => s !== null))]).pipe(
            first(),
            switchMap(([tid, settings]) => {
                const endPoint = settings?.value.firebaseApi + '/budget/paginatedList';
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.auth.appDomain
                });
                if (tid) {
                    return this.http.post<{ data: any[], totalSize: number }>(endPoint, {
                        pageSize,
                        startAt,
                        filter: filterQuery
                    }, {
                        headers: headers
                    });
                } else {
                    throw Error('No token');
                }
            }));
    }

    public getHoursReportByBudgetListFilter(filterQuery: BudgetListFilter) {
        return combineLatest([this.auth.getUserIdToken(), this.configService.settings.pipe(filter(s => s !== null))]).pipe(
            first(),
            switchMap(([tid, settings]) => {
                const endPoint = settings?.value.firebaseApi + '/reporting/hoursReportByBudgetListFilter';
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.auth.appDomain
                });
                if (tid) {
                    return this.http.post<{ fileName: string, wb: any }>(endPoint, {
                        filter: filterQuery
                    }, {
                        headers: headers
                    });
                } else {
                    throw Error('No token');
                }
            }));
    }

    public getSummaryReportByBudgetListFilter(filterQuery: BudgetListFilter) {
        return combineLatest([this.auth.getUserIdToken(), this.configService.settings.pipe(filter(s => s !== null))]).pipe(
            first(),
            switchMap(([tid, settings]) => {
                const endPoint = settings?.value.firebaseApi + '/reporting/reportConsuntiviCommesse';
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.auth.appDomain
                });
                if (tid) {
                    return this.http.post<{ fileName: string, wb: any }>(endPoint, {
                        filter: filterQuery
                    }, {
                        headers: headers
                    });
                } else {
                    throw Error('No token');
                }
            }));
    }

    public getQuickBudgetList() {
        const qf = this.inContext(() => query(collection(this.cDR, BUDGET_COLLECTION), where('searchMode', '==', 'quick')));
        return this.inContext(() => collectionData(qf, {idField: 'id'})) as Observable<Budget[]>;
    }

    public getPendingJobs(budgetId: string) {
        const qf = this.inContext(() => query(collection(this.cDR, BUDGET_RECALCULATE_COLLECTION), where('budgetId', '==', budgetId)));
        return this.inContext(() => collectionData(qf));
    }

    public getBudgetList = (filterIt?: any) => {
        const appUser = this.auth.currentUser;
        let qf;
        if (filterIt && filterIt.status === 'new') {
            if (appUser && appUser.type && appUser.type.includes('cto')) {
                qf = this.inContext(() => query(collection(this.cDR, BUDGET_COLLECTION),
                    where('status', '==', 'new'),
                    where('searchMode', '==', 'quick'),
                    orderBy('creation', 'desc')));
            } else {
                console.log('user', appUser?.googleUid);
                qf = this.inContext(() => query(collection(this.cDR, BUDGET_COLLECTION),
                    where('assignee.googleUid', '==', appUser?.googleUid),
                    where('searchMode', '==', 'quick'),
                    orderBy('creation', 'desc')));
            }
        } else {
            qf = this.inContext(() => query(collection(this.cDR, BUDGET_COLLECTION), where('searchMode', '==', 'quick'), orderBy('creation', 'desc')));
        }
        return this.inContext(() => collectionData(qf, {idField: 'id'})) as Observable<Budget[]>;
    }

    public getDeletedBudgets = () => {
        const budgetCollRef = this.inContext(() => collection(this.cDR, DELETED_BUDGETS));
        return this.inContext(() => collectionData(budgetCollRef, {idField: 'id'})) as Observable<Budget[]>;
    }

    public getOpenProjectsList = () => {
        const qf = this.inContext(() => query(collection(this.cDR, BUDGET_COLLECTION),
            where('status', '==', 'closed'),
            where('searchMode', '==', 'quick'),
            orderBy('creation', 'desc')));
        return this.inContext(() => collectionData(qf, {idField: 'id'})) as Observable<Budget[]>;
    }

    public getNotArchivedBudgetList = () => {
        const budgetCollRef = this.inContext(() => collection(this.cDR, BUDGET_COLLECTION));
        const q = this.inContext(() => query(budgetCollRef, where('status', '!=', 'archived'), orderBy('status', 'desc'), orderBy('creation', 'desc')));
        return this.inContext(() => collectionData(q));
    }

    public getBudgetsForWorkersList() {
        const budgetCollRef = this.inContext(() => collection(this.cDR, BUDGET_COLLECTION));
        const q = this.inContext(() => query(budgetCollRef, where('workerList', '==', true)));
        return this.inContext(() => collectionData(q, {idField: 'id'}));
    }

    public selectForWorkersList(id: string) {
        const budgetDocRef = this.inContext(() => doc(this.cDR, BUDGET_COLLECTION, id));
        return this.inContext(() => updateDoc(budgetDocRef, {workerList: true}));
    }

    public deselectForWorkersList(id: string) {
        const budgetDocRef = this.inContext(() => doc(this.cDR, BUDGET_COLLECTION, id));
        return this.inContext(() => updateDoc(budgetDocRef, {workerList: false}));
    }

    public getProjectList = () => {
        let qf;
        qf = this.inContext(() => query(collection(this.cDR, BUDGET_COLLECTION),
            // .where('project.projectNr', '>', '')
            orderBy('project.projectNr', 'desc'), limit(500)));

        return this._getBudgetCollectionRef(qf).pipe(
            switchMap((bcollection) => {
                return this.inContext(() => collectionData(bcollection));
            }));
    }

    public getBudgetByRef = (ref: any) => {
        return this.inContext(() => docData(ref, {idField: 'id'})) as Observable<Budget>;
    }

    public getSimpleBudgetSummaryBalance(budgetId: string) {
        return this.inContext(() => getDoc(doc(this.cDR, SUMMARY_COLLECTION, budgetId)));
    }

    public getBudgetSummaryBalance(budgetId: string): Observable<SummaryBalanceWrapper> {
        return this.inContext(() => docData(doc(this.cDR, SUMMARY_COLLECTION, budgetId))) as Observable<SummaryBalanceWrapper>;
    }

    public getSimpleBudget(budgetId: string) {
        const itemDoc = this.inContext(() => doc(this.cDR, BUDGET_COLLECTION, budgetId));
        return this.inContext(() => docData(itemDoc)) as Observable<Budget>;
    }

    public getBudget = (bdgtId: string) => {
        const itemDoc = this.inContext(() => doc(this.cDR, BUDGET_COLLECTION, bdgtId));
        return this.inContext(() => docData(itemDoc)).pipe(
            switchMap((b: Budget) => {
                if (!b) {
                    console.error('Budget not found with budgetId', bdgtId);
                    return of(null);
                }
                return of(b);
            }));
    }

    public patchBudget = (bdgtId: string, data: Partial<Budget>) => {
        const itemDoc = this.inContext(() => doc(this.cDR, BUDGET_COLLECTION, bdgtId));
        const appUser = this.auth.currentUser;
        if (!appUser) return;
        data.modifiedBy = {
            displayName: appUser.displayName,
            googleUid: appUser.googleUid,
            email: appUser.email
        };
        data.modifiedOn = new Date();
        return this.inContext(() => updateDoc(itemDoc, data));
    }

    public getBudgetCurrency(budgetId: string) {
        const itemDoc = this.inContext(() => doc(this.cDR, BUDGET_COLLECTION, budgetId));
        return this.inContext(() => docData(itemDoc)).pipe(map((item: Budget) => item?.export ? 'USD' : 'EUR'));
    }

    public patchSubBudget = (bdgtId: string, subBudgetId: string, data: Partial<SubBudget>) => {
        const itemDoc = this.inContext(() => doc(this.cDR, BUDGET_COLLECTION, bdgtId, 'sub-budgets', subBudgetId));
        return this.inContext(() => updateDoc(itemDoc, data));
    }

    public getSubBudgetResume(budgetId: string) {
        const itemDoc = this.inContext(() => doc(this.cDR, BUDGET_COLLECTION, budgetId, 'sub-budgets-resume', 'summary'));
        return this.inContext(() => docData(itemDoc));
    }

    public getSubBudgetCollection = (bdgtId: string) => {
        const subBudgetCollRef = this.inContext(() => collection(this.cDR, BUDGET_COLLECTION, bdgtId, 'sub-budgets'));
        const q = this.inContext(() => query(subBudgetCollRef, orderBy('created')));
        return this.inContext(() => collectionData(q, {idField: 'id'})).pipe(
            debounceTime(500)
        );
    }

    public getAllBudgetActivities = (bdgtId: string) => {
        const subBudgetCollRef = this.inContext(() => collection(this.cDR, BUDGET_COLLECTION, bdgtId, 'sub-budgets'));
        const q = this.inContext(() => query(subBudgetCollRef, orderBy('created')));
        return this.inContext(() => collectionData(q, {idField: 'id'})).pipe(map((subs: any[]) => {
            console.log('----------------------');
            const activeSubs = subs.filter((s) => (s.deleted !== true && s.calculate !== false));
            return Promise.all(activeSubs.map(sub => {
                    return this.inContext(() => getDocs(collection(doc(this.cDR, BUDGET_COLLECTION, bdgtId, 'sub-budgets', sub.id), 'activities')));
                })
            );
        }));
    }

    public getReportSubBudget = (bdgtId: string): Observable<SubBudget[]> => {
        const subBudgetCollRef = this.inContext(() => collection(this.cDR, BUDGET_COLLECTION, bdgtId, 'sub-budgets'));
        const q = this.inContext(() => query(subBudgetCollRef, where('report', '==', true), limit(5)));
        return (this.inContext(() => collectionData(q, {idField: 'id'})) as Observable<SubBudget[]>).pipe(
            map(subs => subs.filter(sub => !sub.id?.startsWith('MERGE_')))
        );
    }

    public setReportSubBudget(bdgtId: string): Observable<any> {
        const param = {bdgtId: bdgtId};
        return this.auth.getUserIdToken().pipe(
            first(),
            switchMap((tid) => {
                const sett = this.configService.settings.value;
                const endPoint = sett.value.firebaseApi + '/budget/setReportSubBudget';
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.auth.appDomain
                });
                if (tid) {
                    return this.http.post(endPoint, param, {
                        headers: headers
                    });
                } else {
                    throw Error('No token');
                }
            }));
    }

    public getSubBudget = (bdgtId: string, subbdgId: string) => {
        const itemDoc = this.inContext(() => doc(this.cDR, BUDGET_COLLECTION, bdgtId, 'sub-budgets', subbdgId));
        return this.inContext(() => docData(itemDoc));
    }

    public upsertSubBudget = (bdgtId: string, subBudgetId: string, data: SubBudget, batch?: any) => {
        const itemDoc = this.inContext(() => doc(this.cDR, BUDGET_COLLECTION, bdgtId, 'sub-budgets', subBudgetId));

        if (!data.createdBy) {
            data.createdBy = AppUserUtil.getUserPartial(this.auth.currentUser);
        }

        if (data instanceof SubBudgetModel) {
            data = data.toPlainObj();
        }

        if (batch) {
            batch.set(itemDoc, data, {merge: true});
            return;
        } else {
            return this.inContext(() => setDoc(itemDoc, data as any, {merge: true}));
        }
    }

    public updateSubBudgetField = (bdgtId: string, subBudgetId: string, data: Partial<SubBudget>) => {
        const itemDoc = this.inContext(() => doc(this.cDR, BUDGET_COLLECTION, bdgtId, 'sub-budgets', subBudgetId));
        return this.inContext(() => updateDoc(itemDoc, data));
    }

    /**
     * Flag the sub budget as deleted
     * @param bdgtId The Budget Id in the collection
     * @param subBudgetId The SubBudget Id in the collection
     * @memberof DataService
     */
    public deleteSubBudget = (bdgtId: string, subBudgetId: string) => {
        console.log('deleting budget: ', bdgtId, subBudgetId);
        const itemDoc = this.inContext(() => doc(this.cDR, BUDGET_COLLECTION, bdgtId, 'sub-budgets', subBudgetId));
        return this.inContext(() => updateDoc(itemDoc, {
            deleted: true
        }));
    }

    /**
     * Copy the subbudget
     * @param bdgtId The Budget Id in the collection
     * @param subBudgetId The SubBudget Id in the collection
     * @param generatedBy The type of user who triggered the copy
     * @memberof DataService
     */
    public copySubBudget = async (bdgtId: string, subBudgetId: string, generatedBy: GeneratedBy) => {
        console.log('Copying sub-budget: ', subBudgetId);
        const subBudgetCollRef = this.inContext(() => collection(this.cDR, BUDGET_COLLECTION, bdgtId, SUBBUDGET_COLLECTION));

        const subBudgetRef = await this.inContext(() => getDoc(doc(subBudgetCollRef, subBudgetId)));
        const subBudget = subBudgetRef.data() as SubBudget;

        console.log('Got original subBudget: ', subBudgetId, subBudget.description, subBudget);
        const id = this.inContext(() => doc(collection(this.firestore, 'id'))).id;
        subBudget.created = this.inContext(() => serverTimestamp());
        subBudget.description = 'Copy of ' + subBudget.description;
        subBudget.createdBy = AppUserUtil.getUserPartial(this.auth.currentUser);
        subBudget.id = id;
        subBudget.generatedBy = generatedBy;
        subBudget.report = false; // There should be only one

        await this.inContext(() => setDoc(doc(subBudgetCollRef, id), subBudget));
        console.log('subBudget set.');

        const activitiesRef = this.inContext(() => collection(doc(subBudgetCollRef, subBudgetId), 'activities'));
        const activities = await this.inContext(() => getDocs(activitiesRef));

        for (const activity of activities.docs) {
            console.log('copying activities: ', activity.id, (activity.data() as Activity));
            const aData = activity.data() as Activity;
            // Reset finals: don't need to be copied
            delete aData.finalHours;
            await this.inContext(() => addDoc(collection(doc(subBudgetCollRef, id), 'activities'), aData));
        }

        const MaterialsRef = this.inContext(() => collection(doc(subBudgetCollRef, subBudgetId), 'materials'));
        const materials = await this.inContext(() => getDocs(MaterialsRef));

        for (const material of materials.docs) {
            console.log('copying material: ', material.id, (material.data() as Material));
            const mData = material.data() as Material;
            // Reset finals: don't need to be copied. This is not needed as should not be there, but to be safe..
            delete (mData as any).finalAmount;
            delete (mData as any).finalQuantity;
            delete (mData as any).processedOrders;
            mData.id = this.inContext(() => doc(collection(this.firestore, 'id'))).id;
            await this.inContext(() => addDoc(collection(doc(subBudgetCollRef, id), 'materials'), mData));
        }

        console.log('SubBudget copied successfully. ', id);
        return id;
    }

    public getSBActivities = (bdgtId: string, subbdgId: string, cMap: Map<string, string>) => {
        const subBudgetActivitiesCollRef = this.inContext(() => collection(this.cDR, BUDGET_COLLECTION, bdgtId, 'sub-budgets', subbdgId, 'activities'));
        return (this.inContext(() => collectionData(subBudgetActivitiesCollRef, {idField: 'id'})) as Observable<Activity[]>).pipe(
            map(activities => [...activities].sort((a, b) => sortActivities(a, b, cMap)))
        );
    }

    public getSBActivity = (bdgtId: string, subbdgId: string, actId: string) => {
        const subBudgetActivityRef = this.inContext(() => doc(this.cDR, BUDGET_COLLECTION, bdgtId, 'sub-budgets', subbdgId, 'activities', actId));
        return this.inContext(() => docData(subBudgetActivityRef));
    }

    public setSBActivity = (bdgtId: string, subbdgId: string, actId: string, data: Activity) => {
        const subBudgetActivityRef = this.inContext(() => doc(this.cDR, BUDGET_COLLECTION, bdgtId, 'sub-budgets', subbdgId, 'activities', actId));
        console.log('Write on', subBudgetActivityRef.path);
        return this.inContext(() => setDoc(subBudgetActivityRef, data as any, {merge: true}));
    }

    public getSBMaterials = (bdgtId: string, subbdgId: string) => {
        const subBudgetMaterialCollRef = this.inContext(() => collection(this.cDR, BUDGET_COLLECTION, bdgtId, 'sub-budgets', subbdgId, 'materials'));
        return (this.inContext(() => collectionData(subBudgetMaterialCollRef, {idField: 'id'})) as Observable<Material[]>).pipe(
            map(materials => [...materials].sort((a, b) => {
                const catA = a.category || '';
                const catB = b.category || '';
                const catComp = catA.localeCompare(catB);
                if (catComp !== 0) {
                    return catComp;
                }
                const descA = a.description || '';
                const descB = b.description || '';
                return descA.localeCompare(descB);
            }))
        );
    }

    public getStorage = () => {
        const storageCollRef = this.inContext(() => collection(this.cDR, STORAGE_COLLECTION));
        return this.inContext(() => collectionData(storageCollRef, {idField: 'id'}));
    }

    public getSBMaterial = (bdgtId: string, subbdgId: string, matId: string) => {
        const subBudgetMaterialRef = this.inContext(() => doc(this.cDR, BUDGET_COLLECTION, bdgtId, 'sub-budgets', subbdgId, 'materials', matId));
        return this.inContext(() => docData(subBudgetMaterialRef));
    }

    public setSBMaterial = (bdgtId: string, subbdgId: string, matId: string, data: Material, batch?: any) => {
        const subBudgetMaterialRef = this.inContext(() => doc(this.cDR, BUDGET_COLLECTION, bdgtId, 'sub-budgets', subbdgId, 'materials', matId));

        if (batch) {
            batch.set(subBudgetMaterialRef, data);
            return;
        } else {
            return this.inContext(() => setDoc(subBudgetMaterialRef, data as any));
        }
    }

    public addMaterial = (budgetId: string, subBudgetId: string, material) => {
        delete material.id;
        const targetSubBudget = this.inContext(() => collection(this.cDR, BUDGET_COLLECTION, budgetId, 'sub-budgets', subBudgetId, 'materials'));
        return from(this.inContext(() => addDoc(targetSubBudget, material)));
    }

    public addStorage = (storage: Storage) => {
        const storageCollRef = this.inContext(() => collection(this.cDR, STORAGE_COLLECTION));
        return from(this.inContext(() => addDoc(storageCollRef, storage as any)));
    }

    public updateMaterial = (budgetId: string, subBudgetId: string, material: Material) => {
        if (!material.id) {
            return of(false);
        }
        const targetMaterial = this.inContext(() => doc(this.cDR, BUDGET_COLLECTION, budgetId, 'sub-budgets', subBudgetId, 'materials', material.id));
        return from(this.inContext(() => updateDoc(targetMaterial, material as any)));
    }

    public updateStorage = (id: string, storage: Storage) => {
        console.log('storageId:', id);
        if (!id) {
            return of(false);
        }
        const targetStorage = this.inContext(() => doc(this.cDR, STORAGE_COLLECTION, id));
        return from(this.inContext(() => updateDoc(targetStorage, storage as any)));
    }

    public deleteMaterial = (budgetId: string, subBudgetId: string, materialId: string) => {
        const material = this.inContext(() => doc(this.cDR, BUDGET_COLLECTION, budgetId, 'sub-budgets', subBudgetId, 'materials', materialId));
        return from(this.inContext(() => deleteDoc(material)));
    }

    public deleteStorage = (storageId: string) => {
        const targetStorage = this.inContext(() => doc(this.cDR, STORAGE_COLLECTION, storageId));
        return from(this.inContext(() => deleteDoc(targetStorage)));
    }

    public deleteBudget = (budgetId: string) => {
        const budgetDocRef = this.inContext(() => doc(this.cDR, BUDGET_COLLECTION, budgetId));
        return from(this.inContext(() => deleteDoc(budgetDocRef)));
    }

    public commitActivities = (budgetId: string, subBudgetId: string, activitiesValue: any) => {
        // console.log('pav', activitiesValue);
        const targetSubBudget = this.inContext(() => collection(this.cDR, BUDGET_COLLECTION, budgetId, 'sub-budgets', subBudgetId, 'activities'));
        const pArray = [];
        for (const [a, h] of Object.entries(activitiesValue)) {
            pArray.push(
                this.inContext(() => updateDoc(doc(targetSubBudget, a), {'hours': h}))
            );
        }
        return from(Promise.all(pArray));
    }

    public createExportBudget = (
        budgetId: string,
        itaBudgetNr: string,
        opportunityId: string,
        companyData: { companyName: string; companyId: string | number; cosmoId: string | null }
    ) => {

        const endPoint = this.settings?.value.firebaseApi + '/budget/createExportBudget';
        return this.auth.getUserIdToken().pipe(
            first(),
            switchMap((tid) => {
                if (tid) {
                    const urlRequest = endPoint;
                    const headers = new HttpHeaders({
                        'Authorization': `Bearer ${tid}`,
                        'X-App-Domain': this.auth.appDomain
                    });
                    return this.http.post(urlRequest, {
                        budgetId: budgetId,
                        itaBudgetNr: itaBudgetNr,
                        opportunityId: opportunityId,
                        companyData: companyData
                    }, {
                        responseType: 'text',
                        headers: headers
                    });
                } else {
                    throw Error('No token');
                }
            }));
    }

    public addSubBudget = (bdgtId: string, description: string, generatedBy: GeneratedBy) => {
        const subBudgetCollRef = this.inContext(() => collection(this.cDR, BUDGET_COLLECTION, bdgtId, 'sub-budgets'));

        // Check if budget is closed
        return this.inContext(() => docData(doc(this.cDR, BUDGET_COLLECTION, bdgtId))).pipe(
            first(),
            switchMap((budget: Budget) => {
                const id = this.inContext(() => doc(collection(this.firestore, 'id'))).id;
                const subBudget: SubBudget = {
                    id,
                    description,
                    created: this.inContext(() => serverTimestamp()),
                    deleted: null,
                    active: true,
                    generatedBy: generatedBy,
                    createdBy: AppUserUtil.getUserPartial(this.auth.currentUser),
                    calculate: true // This determine if the subbudget is included in the total
                };
                if (budget.project) {
                    subBudget.overrideEdit = true;
                }
                return this.inContext(() => setDoc(doc(subBudgetCollRef, id), subBudget as any))
                    .then(() => {
                        // Create the activities from template (this could be moved to BE)
                        this.activitiesTemplate$.value.activities.forEach((a) => {
                            this.inContext(() => addDoc(collection(doc(subBudgetCollRef, id), 'activities'), a));
                        });
                        return true;
                    })
                    .catch((err) => {
                        console.error('Failed while create subbudget', err.message);
                        return false;
                    });
            }));
    }

    public assignBudget = (user: AclUser, bdgtId: string, deliveryDate: Date, noteToPrjMgr: string) => {

        // budget/:budgetId/:userId/:domain/
        const endPoint = this.settings?.value.firebaseApi + '/budget/assignBudget';
        return this.auth.getUserIdToken().pipe(
            first(),
            switchMap((tid) => {
                const userId = user.id;
                if (tid) {
                    const urlRequest = endPoint + '/' + bdgtId;
                    const headers = new HttpHeaders({
                        'Authorization': `Bearer ${tid}`,
                        'X-App-Domain': this.auth.appDomain
                    });
                    return this.http.patch(urlRequest, {
                        userId,
                        domain: this.auth.appDomain,
                        deliveryThr: deliveryDate.getTime(),
                        noteToPrjMgr: noteToPrjMgr
                    }, {
                        responseType: 'text',
                        headers: headers
                    });
                } else {
                    throw Error('No token');
                }
            }));
    }

    public reassignBudget = (user: AclUser, bdgtId: string) => {

        // budget/:budgetId/:userId/:domain/
        const endPoint = this.settings?.value.firebaseApi + '/budget/reassignBudget';
        return this.auth.getUserIdToken().pipe(
            first(),
            switchMap((tid) => {
                // console.log('Got tid', tid);
                const userId = user.id;
                if (tid) {
                    const urlRequest = endPoint + '/' + bdgtId;
                    const headers = new HttpHeaders({
                        'Authorization': `Bearer ${tid}`,
                        'X-App-Domain': this.auth.appDomain
                    });
                    return this.http.patch(urlRequest, {
                        userId,
                        domain: this.auth.appDomain
                    }, {
                        responseType: 'text',
                        headers: headers
                    });
                } else {
                    throw Error('No token');
                }
            }));
    }

    public deleteBudgetApi = (userId: string, budgetId: string) => {

        // budget/:budgetId/:userId/:domain/
        const endPoint = this.settings?.value.firebaseApi + '/budget/delete';
        return this.auth.getUserIdToken().pipe(
            first(),
            switchMap((tid) => {
                if (tid) {
                    const urlRequest = endPoint + '/' + budgetId;
                    const headers = new HttpHeaders({
                        'Authorization': `Bearer ${tid}`,
                        'X-App-Domain': this.auth.appDomain
                    });
                    return this.http.post(urlRequest, {
                        userId,
                        budgetId
                    }, {
                        responseType: 'text',
                        headers: headers
                    });
                } else {
                    throw Error('No token');
                }
            }));
    }

    public openProject = (budgetId: string, projectNr: string, finalOffer: number, projectManager: AclUser,
                          paymentMethod: string, paymentMethodCode: string, deliveryDate: Date) => {
        // createProject/:budgetId/:userId/:projectNr/:orderTotal
        const endPoint = this.settings?.value.firebaseApi + '/budget/createProject';
        const user = this.auth.currentUser;
        // console.log('User is User', user);
        return this.auth.getUserIdToken().pipe(first(), switchMap((tid) => {
            // console.log('Got tid', tid);
            const userId = user.googleUid;
            if (tid) {
                const urlRequest = endPoint + '/' + budgetId;
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.auth.appDomain
                });
                return this.http.patch(urlRequest,
                    {
                        userId,
                        projectNr,
                        finalOffer,
                        projectManager: projectManager.id,
                        paymentMethod,
                        paymentMethodCode,
                        deliveryDate
                    }
                    , {
                        responseType: 'text',
                        headers: headers
                    });
            } else {
                throw Error('No token');
            }
        }));
    }

    public reopenProject = (budgetId: string, status: string) => {
        const endPoint = this.settings?.value.firebaseApi + '/budget/reopenProject';
        const email = this.auth.currentUser.email;
        return this.auth.getUserIdToken().pipe(first(), switchMap((tid) => {
            if (tid) {
                const urlRequest = endPoint + '/' + budgetId;
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.auth.appDomain
                });
                return this.http.patch(urlRequest,
                    {
                        email,
                        budgetId,
                        status
                    }, {
                        responseType: 'text',
                        headers: headers
                    });
            } else {
                throw Error('No token');
            }
        }));
    }

    public createSummaryBalance = (budgetId: string) => {
        const endPoint = this.settings?.value.firebaseApi + '/budget/createSummaryBalance';
        const user = this.auth.currentUser;
        // console.log('User is User', user);
        return this.auth.getUserIdToken().pipe(
            first(),
            switchMap((tid) => {
                console.log('Got tid', tid);
                const userEmail = user.email;
                if (tid) {
                    const urlRequest = endPoint;
                    const headers = new HttpHeaders({
                        'Authorization': `Bearer ${tid}`,
                        'X-App-Domain': this.auth.appDomain
                    });
                    return this.http.post(urlRequest,
                        {
                            userEmail: userEmail,
                            budgetId: budgetId,
                            domainId: this.auth.appDomain
                        }
                        , {
                            responseType: 'text',
                            headers: headers
                        });
                } else {
                    throw Error('No token');
                }
            }));
    }

    public createSummaryPdf = (budgetId) => {
        console.log('CREATING PDF##############');
        const endPoint = this.settings?.value.firebaseApi + '/budget/createSummaryPdf';
        const user = this.auth.currentUser;
        // console.log('User is User', user);
        return this.auth.getUserIdToken().pipe(
            first(),
            switchMap((tid) => {
                console.log('Got tid', tid);
                const userEmail = user.email;
                if (tid) {
                    const urlRequest = endPoint;
                    const headers = new HttpHeaders({
                        'Authorization': `Bearer ${tid}`,
                        'X-App-Domain': this.auth.appDomain
                    });
                    return this.http.post(urlRequest,
                        {
                            userEmail: userEmail,
                            budgetId: budgetId,
                            domainId: this.auth.appDomain
                        }
                        , {
                            responseType: 'text',
                            headers: headers
                        });
                } else {
                    throw Error('No token');
                }
            }));
    }

    public createInternalProject = (projectNr: string, orderTotal: number, description: string, type: string) => {
        // createProject/:budgetId/:userId/:projectNr/:orderTotal

        if (Number.isNaN(parseInt(projectNr, 10))) {
            throw Error('PrjectNr must be parsable as integer');
        }

        const endPoint = this.settings?.value.firebaseApi + '/budget/createInternalProject';
        const user = this.auth.currentUser;
        return this.auth.getUserIdToken().pipe(
            first(),
            switchMap((tid) => {
                // console.log('Got tid', tid);
                // const user = user.uid;
                if (tid) {
                    const urlRequest = endPoint;
                    const headers = new HttpHeaders({
                        'Authorization': `Bearer ${tid}`,
                        'X-App-Domain': this.auth.appDomain
                    });
                    return this.http.post(urlRequest,
                        {
                            user: (({displayName, googleUid, email}) => ({displayName, googleUid, email}))(user),
                            projectNr,
                            orderTotal,
                            description,
                            type
                        }
                        , {
                            responseType: 'text',
                            headers: headers
                        });
                } else {
                    throw Error('No token');
                }
            }));
    }

    public getInternalProjects = () => {
        const q = this.inContext(() => query(
            collection(this.cDR, BUDGET_COLLECTION),
            where('project.internal', '==', true),
            orderBy('project.projectNr', 'desc')));
        return this.inContext(() => collectionData(q, {idField: 'id'}));
    }

    public changeFinalOffer = (budgetInfo, updateObj, auditInfo, currentUser) => {
        const data = {budgetInfo, auditInfo, updateObj, currentUser};
        return this.auth.getUserIdToken().pipe(
            first(),
            switchMap((tid) => {
                const sett = this.configService.settings.value;
                const endPoint = sett.value.firebaseApi + '/budget/changeFinalOffer';
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.auth.appDomain
                });
                if (tid) {
                    return this.http.post(endPoint, data, {
                        headers: headers
                    });
                } else {
                    throw Error('No token');
                }
            }));
    }

    public getHoursReport(fromD: string, to: string): any {
        console.log('getHoursReport ', fromD, to);
        const endPoint = this.settings?.value.firebaseApi + '/budget/getHoursReport';
        return this.auth.getUserIdToken().pipe(first(),
            switchMap((tid) => {
                console.log('Got tid', tid);
                if (tid) {
                    const urlRequest = endPoint;
                    const headers = new HttpHeaders({
                        'Authorization': `Bearer ${tid}`,
                        'X-App-Domain': this.auth.appDomain,
                        'Access-Control-Allow-Origin': '*'
                    });

                    return this.http.post(urlRequest, {
                        from: fromD,
                        to: to
                    }, {
                        responseType: 'json',
                        headers: headers
                    });
                } else {
                    throw Error('No token');
                }
            }),
            first());
    }

    public archiveBudget = (budgetId: string, forceRevenue: boolean, finalInvoiced?) => {
        console.log('CREATING PDF##############');
        const endPoint = this.settings?.value.firebaseApi + '/budget/archiveBudget';
        const user = this.auth.currentUser;
        // console.log('User is User', user);
        return this.auth.getUserIdToken().pipe(
            first(),
            switchMap((tid) => {
                console.log('Got tid', tid);
                const userEmail = user.email;
                if (tid) {
                    const urlRequest = endPoint;
                    const headers = new HttpHeaders({
                        'Authorization': `Bearer ${tid}`,
                        'X-App-Domain': this.auth.appDomain
                    });
                    return this.http.post(urlRequest,
                        {
                            userEmail: userEmail,
                            budgetId: budgetId,
                            domainId: this.auth.appDomain,
                            finalInvoiced: finalInvoiced,
                            forceRevenue: forceRevenue
                        }
                        , {
                            responseType: 'text',
                            headers: headers
                        });
                } else {
                    throw Error('No token');
                }
            }));
    }

    /** Time Report methods */

    public getTimereportsListPaginated(pageSize: number, startAt: number, filterQuery: TimereportListFilter) {
        return combineLatest([this.auth.getUserIdToken(), this.configService.settings.pipe(filter(s => s !== null))]).pipe(
            first(),
            switchMap(([tid, settings]) => {
                const endPoint = settings?.value.firebaseApi + '/timereport/paginatedList';
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.auth.appDomain
                });
                if (tid) {
                    return this.http.post<{ data: any[], totalSize: number }>(endPoint, {
                        pageSize,
                        startAt,
                        filter: filterQuery
                    }, {
                        headers: headers
                    });
                } else {
                    throw Error('No token');
                }
            }));
    }

    public getQuickTimereportsList(filterIt: TimereportListFilter) {
        let q;
        if (filterIt.userEmail) {
            q = this.inContext(() => query(collection(this.cDR, TIMERREPORT_COLLECTION),
                where('user.email', '==', filterIt.userEmail),
                where('date', '>=', filterIt.fromDate),
                where('date', '<=', filterIt.toDate),
                where('searchMode', '==', 'quick')));
        } else {
            q = this.inContext(() => query(collection(this.cDR, TIMERREPORT_COLLECTION),
                where('date', '>=', filterIt.fromDate),
                where('date', '<=', filterIt.toDate),
                where('searchMode', '==', 'quick')));
        }

        return this.inContext(() => collectionData(q, {idField: 'id'})) as Observable<Timereport[]>;
    }

    public getTimereportByBudget = (budgetId: string) => {
        const q = this.inContext(() => query(collection(this.cDR, TIMERREPORT_COLLECTION),
            where('budget.id', '==', budgetId),
            orderBy('date', 'desc')));
        return this.inContext(() => collectionData(q, {idField: 'id'})) as Observable<Timereport[]>;
    }

    public addTimereport = (timereport: Timereport) => {
        const timereportCollRef = this.inContext(() => collection(this.cDR, TIMERREPORT_COLLECTION));
        return this.inContext(() => addDoc(timereportCollRef, timereport as any));
    }

    public updateTimereport = (id: string, timereport: Timereport) => {
        const timereportDocRef = this.inContext(() => doc(this.cDR, TIMERREPORT_COLLECTION, id));
        return this.inContext(() => updateDoc(timereportDocRef, timereport as any));
    }

    public getTimeReport = (id: string): Observable<Timereport> => {
        const timereportDocRef = this.inContext(() => doc(this.cDR, TIMERREPORT_COLLECTION, id));
        return this.inContext(() => docData(timereportDocRef)) as Observable<Timereport>;
    }

    public deleteTimereport = (timereport: Timereport) => {
        const timereportDocRef = this.inContext(() => doc(this.cDR, TIMERREPORT_COLLECTION, timereport.id));
        return this.inContext(() => deleteDoc(timereportDocRef));
    }

    public getReportEmailByUser = (user: AppUser) => {
        const q = this.inContext(() => query(collection(this.cDR, EMAILREPORT_COLLECTION),
            where('user.email', '==', user.email),
            where('success', '==', true)
        ));
        return this.inContext(() => collectionData(q));
    }

    public getNextProjectNr = () => {
        // const projSeq = this.cDR.collection(SEQ_COLLECTION).doc<Sequence>('Project');
        // return projSeq.valueChanges().map((seq) => {
        //   return seq.value + seq.increment;
        // });
        const q = this.inContext(() => query(collection(this.cDR, BUDGET_COLLECTION), orderBy('project.projectNr', 'desc'), limit(80)));
        return this.inContext(() => collectionData(q)).pipe(
            map((budgets: Budget[]) => {
                if (!budgets || !budgets.length) {
                    return '0000';
                }

                // WORKAROUND: skip projectNrs with no-digit chars inside
                const noGoWords = ['5', '6', '7', '8', '9', '10', '4-2020'];
                budgets = budgets.filter(b => {
                    const projectNr = b.project.projectNr;
                    return noGoWords.indexOf(projectNr) === -1 && /^\d+$/.test(projectNr);
                });

                if (!budgets || !budgets.length) {
                    return '0000';
                }
                // console.log('project', budgets);
                const lastPn = parseInt(budgets[0].project.projectNr, 10);
                return (lastPn + 1).toString().padStart(4, '0');
            }));
    }

    public sendDailyReport(data) {
        console.log('Sending daily report with data', data);
        const endPoint = this.settings?.value.firebaseApi + '/timereport/daily';
        return this.auth.getUserIdToken().pipe(
            first(),
            switchMap((tid) => {
                console.log('Got tid', tid);
                const userId = data.user.googleUid;
                if (tid) {
                    const urlRequest = endPoint;
                    const headers = new HttpHeaders({
                        'Authorization': `Bearer ${tid}`,
                        'X-App-Domain': this.auth.appDomain,
                        'Access-Control-Allow-Origin': '*'
                    });
                    // TODO fix this!!!
                    // const myDate = new Date((<Date>data.reportDate).getTime() + 43200000);
                    let myDate = data.reportDate;

                    // We represent the italian day with the italian midnight that day
                    const md = moment(myDate);
                    const dateItaly = moment.tz({
                        year: md.year(),
                        month: md.month(),
                        day: md.date()
                    }, 'Europe/Rome');
                    myDate = dateItaly.toDate();

                    console.log('My Date', myDate);
                    return this.http.post(urlRequest, {
                        userId,
                        date: myDate
                    }, {
                        responseType: 'json',
                        headers: headers
                    });
                } else {
                    throw Error('No token');
                }
            }),
            first());
    }

    public getTimereportsReport(fromD: string, to: string): any {
        console.log('getTimereportsReport ', fromD, to);
        const endPoint = this.settings?.value.firebaseApi + '/timereport/hoursReport';
        return this.auth.getUserIdToken().pipe(
            first(),
            switchMap((tid) => {
                console.log('Got tid', tid);
                if (tid) {
                    const urlRequest = endPoint;
                    const headers = new HttpHeaders({
                        'Authorization': `Bearer ${tid}`,
                        'X-App-Domain': this.auth.appDomain,
                        'Access-Control-Allow-Origin': '*'
                    });

                    return this.http.post(urlRequest, {
                        from: fromD,
                        to: to
                    }, {
                        responseType: 'json',
                        headers: headers
                    });
                } else {
                    throw Error('No token');
                }
            }),
            first());
    }


    public getBatch = () => {
        return this.inContext(() => writeBatch(this.firestore));
    }

    public getInvoicesList = () => {
        console.log('get invoice list call');
        const q = this.inContext(() => query(collection(this.cDR, INVOICES_COLLECTION), orderBy('invoiceDate', 'desc')));
        return this.inContext(() => collectionData(q, {idField: 'id'})) as Observable<Invoice[]>;
    }

    public getQuickInvoicesList() {
        const q = this.inContext(() => query(collection(this.cDR, INVOICES_COLLECTION), where('searchMode', '==', 'quick')));
        return this.inContext(() => collectionData(q, {idField: 'id'})) as Observable<Invoice[]>;
    }

    public getInvoicesListPaginated(pageSize: number, startAt: number, filterQuery: InvoicesLIstFilter) {

        return combineLatest([this.auth.getUserIdToken(), this.configService.settings.pipe(filter(s => s !== null))]).pipe(
            first(),
            switchMap(([tid, settings]) => {
                const endPoint = settings?.value.firebaseApi + '/invoice/paginatedList';
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.auth.appDomain
                });
                if (tid) {
                    return this.http.post<{ data: any[], totalSize: number }>(endPoint, {
                        pageSize,
                        startAt,
                        filter: filterQuery
                    }, {
                        headers: headers
                    });
                } else {
                    throw Error('No token');
                }
            }));
    }

    /***********************/

    public getInvoicesListOnce = () => {
        const q = this.inContext(() => query(collection(this.cDR, INVOICES_COLLECTION), orderBy('invoiceDate', 'desc')));
        return this.inContext(() => getDocs(q));
    }

    public getInvoicesCosmoList = () => {
        const q = this.inContext(() => query(collection(this.cDR, INVOICES_COLLECTION), where('sentToAccounting', '==', true), orderBy('invoiceDate', 'desc')));
        return this.inContext(() => collectionData(q, {idField: 'id'}));
    }

    public getInvoice = (invoicesId: string) => {
        const invoicesRef = this.inContext(() => doc(this.cDR, INVOICES_COLLECTION, invoicesId));
        return this.inContext(() => docData(invoicesRef)) as Observable<Invoice>;
    }

    public getInvoiceLog = (invoicesId: string) => {
        const invoiceLogRef = this.inContext(() => collection(doc(this.cDR, INVOICES_COLLECTION, invoicesId), 'log'));
        return this.inContext(() => collectionData(invoiceLogRef));
    }

    public addInvoices = (invoices: Invoice) => {
        invoices.sentToAccounting = false;
        invoices.registeredToCosmo = 'ready';
        const invoicesCollRef = this.inContext(() => collection(this.cDR, INVOICES_COLLECTION));
        return this.inContext(() => addDoc(invoicesCollRef, invoices as any));
    }

    public updateInvoices = (invoicesId: string, edits: Partial<Invoice>) => {
        console.log('updating', invoicesId, edits);
        const invoiceRef = this.inContext(() => doc(this.cDR, INVOICES_COLLECTION, invoicesId));
        return this.inContext(() => updateDoc(invoiceRef, edits));
    }

    public getDdt = (ddtId: string) => {
        const ddtRef = this.inContext(() => doc(this.cDR, DDT_COLLECTION, ddtId));
        return this.inContext(() => docData(ddtRef));
    }

    public addDdt = (ddt: Ddt) => {
        const ddtCollRef = this.inContext(() => collection(this.cDR, DDT_COLLECTION));
        return this.inContext(() => addDoc(ddtCollRef, ddt as any));
    }

    public updateDdt = (ddtId: string, ddt: Partial<Ddt>) => {
        const ddtRef = this.inContext(() => doc(this.cDR, DDT_COLLECTION, ddtId));
        return this.inContext(() => updateDoc(ddtRef, ddt));
    }

    /***********************/

    public getQuickDdtList() {
        const q = this.inContext(() => query(collection(this.cDR, DDT_COLLECTION), where('searchMode', '==', 'quick')));
        return this.inContext(() => collectionData(q, {idField: 'id'})) as Observable<Ddt[]>;
    }

    public getDdtListPaginated(pageSize: number, startAt: number, filterQuery: DdtListFilter) {

        return combineLatest([this.auth.getUserIdToken(), this.configService.settings.pipe(filter(s => s !== null))]).pipe(
            first(),
            switchMap(([tid, settings]) => {
                const endPoint = settings?.value.firebaseApi + '/ddt/paginatedList';
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.auth.appDomain
                });
                if (tid) {
                    return this.http.post<{ data: any[], totalSize: number }>(endPoint, {
                        pageSize,
                        startAt,
                        filter: filterQuery
                    }, {
                        headers: headers
                    });
                } else {
                    throw Error('No token');
                }
            }));
    }

    /** Users methods     */
    /********************* */

    public getUserList = () => {
        const q = this.inContext(() => query(collection(this.cDR, USER_COLLECTION), where('deleted', '==', false), orderBy('displayName')));
        return this.inContext(() => collectionData(q, {idField: 'id'}));
    }

    public getActiveUserList = () => {
        const q = this.inContext(() => query(collection(this.cDR, USER_COLLECTION), where('deleted', '==', false), where('active', '==', true), orderBy('displayName')));
        return this.inContext(() => collectionData(q, {idField: 'id'}));
    }

    public getUser = (userId: string) => {
        const userRef = this.inContext(() => doc(this.cDR, USER_COLLECTION, userId));
        return this.inContext(() => docData(userRef));
    }

    public addUser = (user: User) => {
        const userCollRef = this.inContext(() => collection(this.cDR, USER_COLLECTION));
        return this.inContext(() => addDoc(userCollRef, user as any));
    }

    public deleteUser = (user: User) => {
        const userRef = this.inContext(() => doc(this.cDR, USER_COLLECTION, user.id));
        return this.inContext(() => updateDoc(userRef, {
            deleted: true
        }));
    }


    /***********************/

    public updateUser = (userId, user: Partial<User>) => {
        const userRef = this.inContext(() => doc(this.cDR, USER_COLLECTION, userId));
        return this.inContext(() => updateDoc(userRef, user));
    }

    /** Roles methods     */
    /********************* */

    public getRoleList = () => {
        const q = this.inContext(() => query(collection(this.cDR, ROLE_COLLECTION), orderBy('permissions', 'asc')));
        return this.inContext(() => collectionData(q, {idField: 'id'}));
    }

    /** UTILITY FUNCTIONS      */
    /************************* */

    public getPaymentMethods = () => {
        const q = this.inContext(() => query(collection(this.cDR, PAYMENTMETHODS_COLLECTION), orderBy('methodValue')));
        return this.inContext(() => collectionData(q, {idField: 'id'})) as Observable<PaymentMethods[]>;
    }

    public getDriveFileById(fileId: string) {
        return this.googleApiService.getDriveFile(fileId);
    }

    public getVendorNda(vendor) {
        try {
            const cfNdaId = this.settings?.value.prosperworks.cfNdaId;

            if (!cfNdaId) {
                throw Error(`nda field id is not defined in settings`);
            }
            console.log('getVendorNda custom fields:', cfNdaId, vendor.custom_fields);
            const vendorNdaFile = vendor.custom_fields.find(cf => cf.custom_field_definition_id.toString() === cfNdaId);

            if (!vendorNdaFile || !vendorNdaFile.value) {
                throw Error(`Vendor nda file not defined`);
            }

            const fileId = this.getFileIdFromDriveUrl(vendorNdaFile.value);

            console.log('File ID of NDA: ', fileId);

            return this.getDriveFileById(fileId);
        } catch (err) {
            console.error('Error while getting vendor nda: ', err);
        }


    }

    /***********************/

    public getBudgetImages(budgetFolder, imageFolderPath): Observable<any> {
        return this.getFolderByPath(budgetFolder, imageFolderPath).pipe(
            switchMap((imageFolder: any) => {
                if (imageFolder) {
                    const query = `'${imageFolder.id}' in parents and trashed = false`;
                    return this.googleApiService.listDriveFiles(query, '*');
                } else {
                    console.log('Folder not found');
                    return of(false);
                }
            })
        );
    }

    getInvoiceItemsWithoutIds() {
        return this.inContext(() => getDocs(collection(this.cDR, INVOICES_COLLECTION))).then(invoices => {

            const invoicesData = invoices.docs.map(invDoc => invDoc.data());

            const result = invoicesData.filter(inv => !inv.items.every(item => item.id !== undefined));

            console.log(result);

            return result;
        });
    }

    public askBudgetModifyAuth(budget: Partial<Budget>): Observable<any> {
        const param = {budget: budget};
        return this.auth.getUserIdToken().pipe(
            first(),
            switchMap((tid) => {
                const sett = this.configService.settings.value;
                const endPoint = sett.value.firebaseApi + '/budget/askBudgetModifyAuth';
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.auth.appDomain
                });
                if (tid) {
                    return this.http.post(endPoint, param, {
                        headers: headers
                    });
                } else {
                    throw Error('No token');
                }
            }));
    }

    /***************************/

    public ackBudgetModifyAuth(budget: Partial<Budget>, userEmail: string, isAuth: boolean): Observable<any> {
        const body = {budget: budget, isAuth: isAuth, userEmail: userEmail};
        return this.auth.getUserIdToken().pipe(
            first(),
            switchMap((tid) => {
                const sett = this.configService.settings.value;
                const endPoint = sett.value.firebaseApi + '/budget/ackBudgetModifyAuth';
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.auth.appDomain
                });
                if (tid) {
                    return this.http.post(endPoint, body, {
                        headers: headers
                    });
                } else {
                    throw Error('No token');
                }
            }));
    }

    public getStatReportsList = (user: AclUser) => {
        const thisStartWeek = moment().startOf('isoWeek').toDate();
        const q = this.inContext(() => query(collection(this.cDR, STATREPORTS_COLLECTION),
            orderBy('creationDate', 'desc'),
            where('user.googleUid', '==', user.googleUid),
            where('creationDate', '<', thisStartWeek),
            limit(50)));
        return this.inContext(() => collectionData(q, {idField: 'id'}));
    }

    public getSingleStatReport = (user: AppUser, date: Date) => {
        const startDate = moment(date).tz('Europe/Rome').startOf('isoWeek').toDate();
        const endDate = moment(date).tz('Europe/Rome').endOf('isoWeek').toDate();
        console.log('getting single stat report:', startDate, endDate, user.email);
        const q = this.inContext(() => query(collection(this.cDR, STATREPORTS_COLLECTION),
            where('user.email', '==', user.email),
            where('creationDate', '>=', startDate),
            where('creationDate', '<=', endDate)));
        return this.inContext(() => collectionData(q, {idField: 'id'}));
    }

    public updateStatReport = (reportId: string, statReport: StatReport) => {
        const statReportRef = this.inContext(() => doc(this.cDR, STATREPORTS_COLLECTION, reportId));
        return this.inContext(() => updateDoc(statReportRef, statReport as any));
    }

    public addStatReport = (statReport: StatReport) => {
        const statReportCollRef = this.inContext(() => collection(this.cDR, STATREPORTS_COLLECTION));
        return this.inContext(() => addDoc(statReportCollRef, statReport as any));
    }

    public deleteStatReport = (reportId: string) => {
        const statReportDocRef = this.inContext(() => doc(this.cDR, STATREPORTS_COLLECTION, reportId));
        return from(this.inContext(() => deleteDoc(statReportDocRef)));
    }

    public getStatFields = () => {
        const roleCollRef = this.inContext(() => collection(this.cDR, STATFIELDS_COLLECTION));
        return this.inContext(() => collectionData(roleCollRef, {idField: 'id'}));
    }

    private _getBudgetCollectionRef = (queryFn?: any) => {
        if (!queryFn) {
            queryFn = this.inContext(() => query(collection(this.firestore, 'unknown'), orderBy('creation', 'desc'), limit(500)));
        }
        console.log('qf', queryFn);
        return this.configService.settings.pipe(
            filter((data) => data !== null),
            map((sett) => {
                const bc = sett.value.budgetCollection;
                return this.inContext(() => collection(this.cDR, bc));
            }));
    }

    /************************/
    /** Stat Report methods */

    private getFileIdFromDriveUrl(driveUrl: string) {
        // https://drive.google.com/open?id=1F9tAFBVE3DoCPT8KngEAxQqUZnGMigzj
        // OR https://drive.google.com/file/d/1BexUs7KGhAHrSDnkL-1boW1DtnlFDNbn/view?usp=share_link

        let tokens = driveUrl.split(`?id=`);
        console.log('first url split attempt:', tokens);
        if (tokens.length === 2) {
            return tokens[1];
        }

        tokens = driveUrl.split(`/`);
        console.log('second url split attempt:', tokens);
        if (tokens.length > 5) {
            return tokens[5];
        }

        return '';
    }

    private getFolderByPath(rootId: string, path: string): Observable<any> {
        const currentFolder = path.split('/')[0];
        const query = `'${rootId}' in parents and name = '${currentFolder}' and trashed = false`;

        return this.googleApiService.listDriveFiles(query).pipe(
            switchMap((resp: any) => {
                if (resp.files.length === 0) {
                    return of(false);
                } else if (path.split('/').length === 1) {
                    return of(resp.files[0]);
                } else {
                    return this.getFolderByPath(resp.files[0].id, path.replace(currentFolder + '/', ''));
                }
            })
        );
    }

    public getLatestActivityBluePrints = () => {
        const q = this.inContext(() => query(collection(this.cDR, '/Blueprints/ActivitiesTemplate/ActivitiesHistory'), where('latest', '==', true)));
        return this.inContext(() => collectionData(q, {idField: 'id'}))
            .pipe(map((aat: any) => aat[0]));
    }

    public addLatestActivityBluePrints = (actT: ActivitiesTemplate) => {
        return this.inContext(() => updateDoc(doc(this.cDR, '/Blueprints/ActivitiesTemplate/ActivitiesHistory', (actT as any).id), {
            latest: false,
            validTo: new Date()
        }))
            .then(() => {
                delete (actT as any).id;
                return this.inContext(() => addDoc(collection(this.cDR, '/Blueprints/ActivitiesTemplate/ActivitiesHistory'), actT as any));
            });
    }

    public getLatestMarginsBluePrints = (isExport: boolean) => {
        const collectionName = !isExport ? 'MarginsHistory' : 'MarginsHistoryExport';
        const q = this.inContext(() => query(collection(this.cDR, '/Blueprints/MarginsTemplate/' + collectionName), where('latest', '==', true)));
        return this.inContext(() => collectionData(q, {idField: 'id'}))
            .pipe(map((aat: any) => aat[0]));
    }

    public addLatestMarginsBluePrints = async (template: MarginsTemplate, isExport: boolean) => {

        const collectionName = isExport ? 'MarginsHistoryExport' : 'MarginsHistory';
        console.log(`Updating margins default, is export ? ${isExport}: ${collectionName}`, template);
        if ((template as any).id) {
            await this.inContext(() => updateDoc(doc(this.cDR, '/Blueprints/MarginsTemplate/' + collectionName, (template as any).id), {
                latest: false,
                validTo: new Date()
            }));
        }
        delete (template as any).id;
        return this.inContext(() => addDoc(collection(this.cDR, '/Blueprints/MarginsTemplate/' + collectionName), template as any));
    }

    /**
     * @param fromDate YYYY-MM-DD date
     * @param toDate YYYY-MM-DD date
     */
    public generateReportOreCommesse(fromDate: string, toDate: string) {
        return this.auth.getUserIdToken().pipe(
            first(),
            switchMap((tid) => {
                const sett = this.configService.settings.value;
                const endPoint = sett.value.firebaseApi + '/reporting/reportOreCommesse';
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.auth.appDomain
                });
                if (tid) {
                    return this.http.post(endPoint, {from: fromDate, to: toDate}, {
                        headers: headers
                    });
                } else {
                    throw Error('No token');
                }
            }));
    }

    public getReportDownloadLink(id: string) {

        return this.auth.getUserIdToken().pipe(
            first(),
            switchMap((tid) => {
                const sett = this.configService.settings.value;
                const endPoint = sett.value.firebaseApi + '/reporting/getDownloadLink/' + id;
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.auth.appDomain
                });
                if (tid) {
                    return this.http.get<{ url: string }>(endPoint, {
                        headers: headers
                    });
                } else {
                    throw Error('No token');
                }
            }));
    }

    public listReportingFiles = () => {
        const q = this.inContext(() => query(collection(this.cDR, '/ReportFiles'), orderBy('createdAt', 'desc')));
        return this.inContext(() => collectionData(q, {idField: 'id'}));
    }

    // ===== OPPORTUNITIES =========================================================================
    public async createNewOpportunity(opportunity: Opportunity, createBudget: boolean) {
        return this.auth.getUserIdToken().pipe(
            first(),
            switchMap(tid => {
                const settings = this.configService.settings.value;
                const route = settings?.value.firebaseApi + '/budget/';
                const endPoint = createBudget ?
                    route.concat('createOpportunityAndBudget') : route.concat('createOnlyOpportunity');
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.auth.appDomain
                });
                return this.http.post(endPoint, {opportunity: opportunity}, {headers: headers});
            })
        ).toPromise();
    }

    public getOpportunitiesList() {
        return this.opportunities$;
    }

    public getFollowUpsListByOpportunityId(opportunityId: string) {
        const q = this.inContext(() => query(collection(this.cDR, FOLLOW_UPS_COLLECTION),
            where('opportunityId', '==', opportunityId),
            orderBy('followUpDate', 'desc')
        ));
        return this.inContext(() => collectionData(q, {idField: 'id'})) as Observable<FollowUp[]>;
    }

    public getOpportunityByOpportunityId(opportunityId: string) {
        return this.inContext(() => docData(doc(this.cDR, OPPORTUNITIES_COLLECTION, opportunityId), {idField: 'id'})) as Observable<Opportunity>;
    }

    public getOpportunityByMergedBudgetId(mergedBudgetId: string): Observable<Opportunity[]> {
        const qf = this.inContext(() => query(collection(this.cDR, OPPORTUNITIES_COLLECTION), where('mergedBudgetId', '==', mergedBudgetId)));
        return this.inContext(() => collectionData(qf, {idField: 'id'})) as Observable<Opportunity[]>;
    }

    public getOpportunityByOpportunityIdSingle(opportunityId: string) {
        return from(this.inContext(() => getDoc(doc(this.cDR, OPPORTUNITIES_COLLECTION, opportunityId))))
            .pipe(map(document => {
                return {...document.data(), id: document.id} as Opportunity;
            }));
    }

    createNewFollowUp(followUp: FollowUp) {
        return this.inContext(() => runTransaction(this.firestore, async (transaction) => {
            const newFollowUpRef = doc(collection(this.cDR, FOLLOW_UPS_COLLECTION));
            transaction.set(newFollowUpRef, followUp);

            if (followUp.previous) {
                transaction.update(doc(this.cDR, FOLLOW_UPS_COLLECTION, followUp.previous), {latest: false});
            }

            const updateObj: Partial<Opportunity> = {
                status: followUp.status,
                opportunityStage: followUp.opportunityStage,
                proposalSentOn: followUp.proposalSentOn,
                proposalRequested: followUp.proposalRequested,
                nextFollowUpDate: followUp.nextFollowUpDate,
                nextSteps: followUp.nextSteps,
                potentialClosureYear: followUp.potentialClosureYear,
                potentialClosureQuarter: followUp.potentialClosureQuarter,
                revisionNumber: followUp.revisionNumber,
                lostBecause: followUp.lostBecause,
                forecastAmountUSA: followUp.forecastAmountUSA,
                forecastAmountITA: followUp.forecastAmountITA,
                latestFollowUp: newFollowUpRef.id,
                followUpDate: followUp.followUpDate
            };

            transaction.update(doc(this.cDR, OPPORTUNITIES_COLLECTION, followUp.opportunityId), updateObj);
        }));
    }

    async editFollowUp(followUp: FollowUp) {
        this.inContext(() => runTransaction(this.firestore, async (transaction) => {
            if (!followUp.id) return;
            transaction.update(doc(this.cDR, FOLLOW_UPS_COLLECTION, followUp.id), followUp as any);

            const updateObj: Partial<Opportunity> = {
                status: followUp.status,
                opportunityStage: followUp.opportunityStage,
                proposalSentOn: followUp.proposalSentOn,
                proposalRequested: followUp.proposalRequested,
                nextFollowUpDate: followUp.nextFollowUpDate,
                nextSteps: followUp.nextSteps,
                potentialClosureYear: followUp.potentialClosureYear,
                potentialClosureQuarter: followUp.potentialClosureQuarter,
                revisionNumber: followUp.revisionNumber,
                lostBecause: followUp.lostBecause,
                forecastAmountUSA: followUp.forecastAmountUSA,
                forecastAmountITA: followUp.forecastAmountITA,
                latestFollowUp: followUp.id,
                followUpDate: followUp.followUpDate
            };


            transaction.update(doc(this.cDR, OPPORTUNITIES_COLLECTION, followUp.opportunityId), updateObj);
        }));
    }

    public async deleteFollowUp(followUp: FollowUp) {
        return this.inContext(() => runTransaction(this.firestore, async (transaction) => {
            if (followUp.previous) {
                const previousFollowUpRef = doc(this.cDR, FOLLOW_UPS_COLLECTION, followUp.previous);
                const previousFollowUp: any = (await transaction.get(previousFollowUpRef)).data();

                transaction.update(previousFollowUpRef, {latest: true});

                console.log('previous follow up', previousFollowUp);
                const updateObj: Partial<Opportunity> = {
                    status: previousFollowUp.status,
                    opportunityStage: previousFollowUp.opportunityStage,
                    proposalSentOn: previousFollowUp.proposalSentOn,
                    nextFollowUpDate: previousFollowUp.nextFollowUpDate,
                    nextSteps: previousFollowUp.nextSteps,
                    potentialClosureYear: previousFollowUp.potentialClosureYear,
                    potentialClosureQuarter: previousFollowUp.potentialClosureQuarter,
                    revisionNumber: previousFollowUp.revisionNumber,
                    lostBecause: previousFollowUp.lostBecause,
                    forecastAmountUSA: previousFollowUp.forecastAmountUSA,
                    forecastAmountITA: previousFollowUp.forecastAmountITA,
                    latestFollowUp: previousFollowUpRef.id,
                    followUpDate: previousFollowUp.followUpDate
                };

                const oppRef = doc(this.cDR, OPPORTUNITIES_COLLECTION, followUp.opportunityId);
                transaction.update(oppRef, updateObj);

                transaction.delete(doc(this.cDR, FOLLOW_UPS_COLLECTION, followUp.id));
            }
        }));
    }

    public async deleteOpportunity(opportunity: Opportunity) {
        if (!opportunity || !opportunity.id) {
            console.error('CRITICAL: deleteOpportunity called with an invalid opportunity object.', opportunity);
            throw new Error('Cannot delete opportunity without a valid ID.');
        }

        const opportunityRef = this.inContext(() => doc(this.cDR, OPPORTUNITIES_COLLECTION, opportunity.id));

        const followUpsCollectionRef = this.inContext(() => collection(this.cDR, FOLLOW_UPS_COLLECTION));
        const q = this.inContext(() => query(followUpsCollectionRef, where('opportunityId', '==', opportunity.id)));
        const followUpsRef = await this.inContext(() => getDocs(q));

        return this.inContext(() => runTransaction(this.firestore, async (transaction) => {

            console.log(`Starting transaction to delete opportunity: ${opportunity.id}`);

            console.log(`Found ${followUpsRef.docs.length} follow-up(s) to delete.`);
            for (const d of followUpsRef.docs) {
                console.log(`Deleting FU... ${d.id} / `, d.data()?.opportunityId);
                transaction.delete(d.ref);
            }

            console.log(`Deleting opportunity: ${opportunity.id}`);
            transaction.delete(opportunityRef);
        }));
    }

    public async changeOpportunityName(opportunity: Opportunity, newName: string) {
        await this.inContext(() => updateDoc(doc(this.cDR, OPPORTUNITIES_COLLECTION, opportunity.id), {opportunityName: newName}));
        if (opportunity.budgetId) {
            await this.inContext(() => updateDoc(doc(this.cDR, BUDGET_COLLECTION, opportunity.budgetId), {budgetName: newName}));
        }
    }

    public async changeOpportunityCompany(opportunity: Opportunity, company: any, cosmoId: string | null) {
        const companyData = {
            companyName: company.name,
            companyId: company.id,
            cosmoId: cosmoId ?? null
        };
        const oppUpdate: any = {
            companyName: company.name,
            companyId: company.id,
            contactId: company.primary_contact_id ?? null
        };
        if (company.address?.country) {
            oppUpdate.country = company.address.country;
        }

        await this.inContext(() => updateDoc(doc(this.cDR, OPPORTUNITIES_COLLECTION, opportunity.id), oppUpdate));

        if (opportunity.budgetId) {
            const budgetRef = doc(this.cDR, BUDGET_COLLECTION, opportunity.budgetId);
            await this.inContext(() => updateDoc(budgetRef, {
                company: companyData,
                companyName: company.name
            }));

            try {
                const budgetSnap = await this.inContext(() => getDoc(budgetRef));
                if (budgetSnap.exists()) {
                    const bData: any = budgetSnap.data();
                    if (bData?.exportBudget) {
                        const exportBudgetId = typeof bData.exportBudget === 'string'
                            ? bData.exportBudget.split('/').pop()
                            : bData.exportBudget.id;
                        if (exportBudgetId) {
                            await this.inContext(() => updateDoc(doc(this.cDR, BUDGET_COLLECTION, exportBudgetId), {
                                company: companyData,
                                companyName: company.name
                            }));
                        }
                    }
                    if (bData?.export?.budget) {
                        const itaBudgetId = typeof bData.export.budget === 'string'
                            ? bData.export.budget.split('/').pop()
                            : bData.export.budget.id;
                        if (itaBudgetId) {
                            await this.inContext(() => updateDoc(doc(this.cDR, BUDGET_COLLECTION, itaBudgetId), {
                                company: companyData,
                                companyName: company.name
                            }));
                        }
                    }
                }
            } catch (err) {
                console.warn('Could not update linked budget company:', err);
            }
        }
    }

    public async changeOpportunityBudget(opportunityId: string, oldBudgetId: string, newBudgetId: string, newBudgetNr: string) {
        await this.inContext(() => updateDoc(doc(this.cDR, OPPORTUNITIES_COLLECTION, opportunityId), {
            budgetId: newBudgetId,
            budgetNr: newBudgetNr
        }));
        if (oldBudgetId) {
            await this.inContext(() => updateDoc(doc(this.cDR, BUDGET_COLLECTION, oldBudgetId), {opportunityIds: []}));
        }
        return this.inContext(() => updateDoc(doc(this.cDR, BUDGET_COLLECTION, newBudgetId), {opportunityIds: [opportunityId]}));
    }

    public assignSalesManager(opportunity: Opportunity, email: string) {
        return this.inContext(() => updateDoc(doc(this.cDR, OPPORTUNITIES_COLLECTION, opportunity.id), {salesManager: email}));
    }

    public searchCompany(searchString: string) {
        return this.auth.getUserIdToken().pipe(
            first(),
            switchMap(tid => {
                const settings = this.configService.settings.value;
                const endPoint = settings?.value.firebaseApi + '/contacts/search/' + searchString;
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.auth.appDomain
                });
                return this.http.get(endPoint, {headers: headers});
            })
        );
    }

    public getCompanyDetailsById(id: number) {
        return this.auth.getUserIdToken().pipe(
            first(),
            switchMap(tid => {
                const settings = this.configService.settings.value;
                const endPoint = settings?.value.firebaseApi + '/contacts/companies/' + id;
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.auth.appDomain
                });
                return this.http.get(endPoint, {headers: headers});
            })
        );
    }

    // =============================================================================================

    // ===== BUDGETS ================================================================================

    public createBudgetFromOpportunity(opportunity: any, cosmoId: string | null) {
        return this.auth.getUserIdToken().pipe(
            first(),
            switchMap(tid => {
                const settings = this.configService.settings.value;
                const endPoint = settings?.value.firebaseApi + '/budget/createBudgetFromOpportunity';
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.auth.appDomain
                });

                return this.http.post(endPoint, {opportunity: opportunity, cosmoId: cosmoId}, {headers: headers});
            })
        );
    }

    public addScopesToBudget(budget: Budget, scopes: any[]) {
        return this.inContext(() => runTransaction(this.firestore, async (transaction) => {
            this.patchBudget(budget.id, {scope: scopes});

            if (budget.opportunityIds && budget.opportunityIds[0]) {
                const oppRef = doc(this.cDR, OPPORTUNITIES_COLLECTION, budget.opportunityIds[0]);
                const opp = await transaction.get(oppRef);
                if (opp.exists()) {
                    transaction.update(oppRef, {scopes: scopes});
                }
            }
        }));
    }

    public mergeBudgets = (sourceBudgetId: string, destinationBudgetId: string, sourceBudgetNr: string) => {
        return this.auth.getUserIdToken().pipe(
            first(),
            switchMap(tid => {
                const settings = this.configService.settings.value;
                const endPoint = settings?.value.firebaseApi + '/budget/mergeBudgets';
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.auth.appDomain
                });

                return this.http.post(endPoint,
                    {
                        sourceBudgetId: sourceBudgetId,
                        destinationBudgetId: destinationBudgetId,
                        sourceBudgetNr: sourceBudgetNr
                    },
                    {headers: headers});
            })
        );
    }

    // ==============================================================================================
}